import { getFields } from "97Parts/Andon/logFields";
import {
  FormatDate,
  LeakageExpression,
  SensorStatusType,
  TypeDetailsList,
  TypeKizaiKind,
} from "99Tools/utils";

/**
 * 表コンポーネント用共通パラメタ
 */
export class ListParams {
  public bShowDetailDlg = false;
  public page = 0;
  public rowsPerPage = 10;
  public reset() {
    this.bShowDetailDlg = false;
    this.page = 0;
    // 表示件数設定は残す
  }
}

/**
 * 詳細画像をやり取りするためのインターフェース
 */
export interface IFMapImage {
  setFileName(name: string): void;
  getFileName(): string;
  setFileExtension(extension: string): void;
  setData(data: string): void;
  setError(err: any, message: string): void;
  clear(): void;
}

export class LoginUser {
  public UserId: string;
  public UserName: string;
  public Email: string; // 削除処理用
  constructor(data: any) {
    this.UserId = data?.UserId ?? "未承認ユーザー";
    this.UserName = data
      ? `${data.family_name ?? ""} ${data.given_name ?? ""}`
      : "";
    this.Email = data?.Email ?? data?.email ?? "";
  }
}

// アンドンページに渡すデータの共通インターフェース
export type AndonDataType = "buildings" | "sensors" | "pico3";
export type AndonSortOrder = "asc" | "desc";
export type AndonParamName =
  | "STATUS"
  | "建物名"
  | "端末名称"
  | "設置場所"
  | "更新日時";
export type AndonSortParam = {
  target: AndonDataType;
  param: AndonParamName;
  order: AndonSortOrder;
};
export interface IFAndonData {
  buildings: BuildingInfo[];
  sensors: Sensor[];
  pico3: Pico3[];
  filter: AndonFilterSettings;
  sortParamBuilding: AndonSortParam;
  sortParamSensor: AndonSortParam;
  sortParamPico3: AndonSortParam;
  ResetFilter: (target: AndonDataType, flag: boolean, colIndex: number) => void;
  ApplyFilter: (target: AndonDataType) => void;
  UpdateAllCheck: (target: AndonDataType, colIndex: number) => void;
  Sort: (param: AndonSortParam) => void;
}

export class BuildingInfo implements IFMapImage {
  public BuildingId: string;
  public BuildingName: string;
  public PolicyNumber: string;
  public BuildingAddress: string;
  public UserId: string;
  public Email: string;
  public BuildingImage: string;
  public sensorStatus: SensorStatusType = LeakageExpression["DUMMY"];
  public errorMessage: string;
  public bShowTop: boolean = true; // トップページフィルタ用
  public originalOrder: number = 0; // ソート用
  public ImageFileName: string; // 画像アップロード用、画像ファイル名
  public ImageEncoded: string; // 画像アップロード用、Base64エンコード済文字列
  public ImageExtension: string; //画像アップロード用、ファイル拡張子

  constructor(data: any) {
    this.BuildingId = data?.BuildingId ?? "";
    this.BuildingName = data?.BuildingName ?? "";
    this.PolicyNumber = data?.PolicyNumber ?? "";
    this.BuildingAddress = data?.BuildingAddress ?? "";
    this.UserId = data?.UserId ?? "";
    this.Email = data?.email ?? "";
    this.BuildingImage = data?.BuildingImage ?? "";
    this.errorMessage = "";
    this.ImageFileName = "";
    this.ImageEncoded = "";
    this.ImageExtension = "";
  }
  public Copy() {
    const c = new BuildingInfo(null);
    c.BuildingId = this.BuildingId.slice();
    c.BuildingName = this.BuildingName.slice();
    c.PolicyNumber = this.PolicyNumber.slice();
    c.BuildingAddress = this.BuildingAddress.slice();
    c.BuildingImage = this.BuildingImage.slice();
    return c;
  }
  public GetDetails(): TypeDetailsList {
    return [
      { title: "建物名", value: this.BuildingName },
      { title: "住所", value: this.BuildingAddress },
    ];
  }
  public SetSensorStatus(sensors: Sensor[]) {
    // 建物に設置されているデバイス中、最優先の通知アイコンを検索
    sensors
      .filter((sensor: Sensor) => sensor.BuildingId === this.BuildingId)
      .forEach((sensor: Sensor) => {
        const e = sensor.GetLeakageExpression();
        if (this.sensorStatus.priority < e.priority) {
          this.sensorStatus = e;
        }
      });
  }
  // IFMapImage
  setFileName(name: string): void {
    this.ImageFileName = name;
  }
  getFileName(): string {
    return this.ImageFileName;
  }
  setFileExtension(extension: string): void {
    this.ImageExtension = extension;
  }
  setData(data: string): void {
    this.ImageEncoded = data;
  }
  setError(err: any, message: string): void {
    this.errorMessage = message;
    console.log(this.errorMessage);
    console.error(err);
  }
  clear() {
    this.errorMessage = "";
    this.ImageFileName = "";
    this.ImageEncoded = "";
    this.ImageExtension = "";
  }
}

export class Sensor implements IFMapImage {
  public BuildingId: string;
  public BuildingName: string = "";
  public UpdatedAt: string;
  public PicoId: string;
  public LastNoticed: string;
  public ValidItem: string;
  public SensorType: string;
  public SensorId: string;
  public SensorName: string;
  public LocationName: string;
  public NoticeInterval: string;
  public ComStatus: string;
  public LastLeaked: string;
  public LatestLog: any;
  public CreatedAt: string;
  public SensorImage: string;
  public bShowTop: boolean = true; // トップページフィルタ用
  public originalOrder: number = 0; // ソート用
  public errorMessage: string;
  public ImageFileName: string; // 画像アップロード用、画像ファイル名
  public ImageEncoded: string; // 画像アップロード用、Base64エンコード済文字列
  public ImageExtension: string; //画像アップロード用、ファイル拡張子
  public DataRoute: string[];
  public StartDate: string;
  public ExpirationDate: string;
  public IconStatus: string;

  constructor(data: any) {
    this.BuildingId = data?.BuildingId ?? "";
    this.UpdatedAt = data?.UpdatedAt ?? "";
    this.PicoId = data?.PicoId ?? "";
    this.LastNoticed = data?.LastNoticed ?? "";
    this.ValidItem = data?.ValidItem ?? "";
    this.SensorType = data?.SensorType ?? "";
    this.SensorId = data?.SensorId ?? "";
    this.SensorName = data?.SensorName ?? "";
    this.LocationName = data?.LocationName ?? "";
    this.NoticeInterval = data?.NoticeInterval ?? "30";
    this.ComStatus = data?.ComStatus ?? "";
    this.LastLeaked = data?.LastLeaked ?? "";
    this.LatestLog = data?.LatestLog ?? {};
    this.CreatedAt = data?.CreatedAt ?? "";
    this.SensorImage = data?.SensorImage ?? "";
    this.errorMessage = "";
    this.ImageFileName = "";
    this.ImageEncoded = "";
    this.ImageExtension = "";
    this.DataRoute = data?.DataRoute ?? [];
    this.StartDate = data?.StartDate ?? "";
    this.ExpirationDate = data?.ExpirationDate ?? "";
    this.IconStatus = data?.IconStatus ?? "";
  }
  public Copy() {
    const c = new Sensor(null);
    c.BuildingId = this.BuildingId.slice();
    c.UpdatedAt = this.UpdatedAt.slice();
    c.PicoId = this.PicoId.slice();
    c.LastNoticed = this.LastNoticed.slice();
    c.ValidItem = this.ValidItem.slice();
    c.SensorType = this.SensorType.slice();
    c.SensorId = this.SensorId.slice();
    c.SensorName = this.SensorName.slice();
    c.LocationName = this.LocationName.slice();
    c.NoticeInterval = this.NoticeInterval.slice();
    c.ComStatus = this.ComStatus.slice();
    c.LastLeaked = this.LastLeaked.slice();
    c.LatestLog = JSON.parse(JSON.stringify(this.LatestLog));
    c.CreatedAt = this.CreatedAt.slice();
    c.SensorImage = this.SensorImage.slice();
    c.DataRoute = JSON.parse(JSON.stringify(this.DataRoute));
    c.StartDate = this.StartDate.slice();
    c.ExpirationDate = this.ExpirationDate.slice();
    c.IconStatus = this.IconStatus.slice();
    return c;
  }
  public GetDetails(): TypeDetailsList {
    const items = [
      { title: "種別", value: this.SensorType },
      { title: "機材ID", value: this.SensorId },
      { title: "端末名称", value: this.SensorName },
      { title: "設置場所", value: this.LocationName },
      { title: "バージョン", value: this.LatestLog?.SensorVer },
    ];
    if (this.SensorType === "SubGiga") {
      items.push({
        title: "DataRoute",
        value: this.DataRoute as any,
      });
    }
    return items;
  }
  public GetLeakageExpression() {
    if (this.IconStatus && this.IconStatus === "expired") {
      // 使用期限切れ
      return LeakageExpression["EXPIRED"];
    } else if (
      this.LatestLog.SensorInfo === "LEAKOFF" &&
      this.IconStatus &&
      this.IconStatus === "expiring"
    ) {
      // 使用期限切れ間近は漏水OFFの場合のみ
      return LeakageExpression["PRE_EXPIRED"];
    } else {
      if (this.ComStatus.toLowerCase() === "disconnection") {
        return LeakageExpression["DISCONNECTION"];
      } else if (!LeakageExpression[this.LatestLog.SensorInfo]) {
        // 存在しない STATUS
        return LeakageExpression["ILLEGAL"];
      }
      return LeakageExpression[this.LatestLog.SensorInfo];
    }
  }
  public StatusUpdatedAt() {
    return FormatDate(this.UpdatedAt);
  }
  public UseStarted() {
    return FormatDate(this.StartDate);
  }
  public Expiration() {
    return FormatDate(this.ExpirationDate);
  }
  public SetBuildingName(buildings: BuildingInfo[]) {
    const building = buildings.find(
      (building: BuildingInfo) => building.BuildingId === this.BuildingId
    );
    this.BuildingName = building?.BuildingName ?? "";
  }
  // IFMapImage
  setFileName(name: string): void {
    this.ImageFileName = name;
  }
  getFileName(): string {
    return this.ImageFileName;
  }
  setFileExtension(extension: string): void {
    this.ImageExtension = extension;
  }
  setData(data: string): void {
    this.ImageEncoded = data;
  }
  setError(err: any, message: string): void {
    this.errorMessage = message;
    console.log(this.errorMessage);
    console.error(err);
  }
  clear() {
    this.errorMessage = "";
    this.ImageFileName = "";
    this.ImageEncoded = "";
    this.ImageExtension = "";
  }
}

export class Pico3 implements IFMapImage {
  public ItemType: string;
  public UpdatedAt: string;
  public PicoName: string;
  public PicoId: string;
  public LocationName: string;
  public ValidItem: string;
  public BuildingId: string;
  public BuildingName: string = "";
  public ComStatus: string;
  public LatestLog: any;
  public CreatedAt: string;
  public PicoImage: string;
  public bShowTop: boolean = true; // トップページフィルタ用
  public originalOrder: number = 0; // ソート用
  public errorMessage: string;
  public ImageFileName: string; // 画像アップロード用、画像ファイル名
  public ImageEncoded: string; // 画像アップロード用、Base64エンコード済文字列
  public ImageExtension: string; //画像アップロード用、ファイル拡張子

  constructor(data: any) {
    this.ItemType = data?.ItemType ?? "";
    this.UpdatedAt = data?.UpdatedAt ?? "";
    this.PicoName = data?.PicoName ?? "";
    this.PicoId = data?.PicoId ?? "";
    this.LocationName = data?.LocationName ?? "";
    this.ValidItem = data?.ValidItem ?? "";
    this.BuildingId = data?.BuildingId ?? "";
    this.ComStatus = data?.ComStatus ?? "";
    this.LatestLog = data?.LatestLog ?? {};
    this.CreatedAt = data?.CreatedAt ?? "";
    this.PicoImage = data?.PicoImage ?? "";
    this.errorMessage = "";
    this.ImageFileName = "";
    this.ImageEncoded = "";
    this.ImageExtension = "";
  }
  public Copy() {
    const c = new Pico3(null);
    c.ItemType = this.ItemType.slice();
    c.UpdatedAt = this.UpdatedAt.slice();
    c.PicoName = this.PicoName.slice();
    c.PicoId = this.PicoId.slice();
    c.LocationName = this.LocationName.slice();
    c.ValidItem = this.ValidItem.slice();
    c.BuildingId = this.BuildingId.slice();
    c.ComStatus = this.ComStatus.slice();
    c.LatestLog = JSON.parse(JSON.stringify(this.LatestLog));
    c.CreatedAt = this.CreatedAt.slice();
    c.PicoImage = this.PicoImage.slice();
    return c;
  }
  public GetDetails(): TypeDetailsList {
    return [
      { title: "端末名称", value: this.PicoName },
      { title: "機材ID", value: this.PicoId },
      { title: "設置場所", value: this.LocationName },
    ];
  }
  public GetLatestLog(): TypeDetailsList {
    const result: TypeDetailsList = [];
    getFields("pico3").forEach((item) => {
      if (this.LatestLog[item] !== undefined) {
        result.push({ title: item, value: this.LatestLog[item] });
      }
    });
    return result;
  }
  public GetLeakageExpression() {
    // 注: センサとpico3は判定方法が異なる
    if (this.ComStatus.toLowerCase() === "normal") {
      return LeakageExpression["OK"];
    } else if (this.ComStatus.toLowerCase() === "disconnection") {
      return LeakageExpression["DISCONNECTION"];
    } else if (!LeakageExpression[this.LatestLog.SensorInfo]) {
      // 存在しない STATUS
      return LeakageExpression["ILLEGAL"];
    }
    return LeakageExpression[this.LatestLog.SensorInfo];
  }
  public StatusUpdatedAt() {
    return FormatDate(this.LatestLog.ReceiveTime);
  }
  public SetBuildingName(buildings: BuildingInfo[]) {
    const building = buildings.find(
      (building: BuildingInfo) => building.BuildingId === this.BuildingId
    );
    this.BuildingName = building?.BuildingName ?? "";
  }
  // IFMapImage
  setFileName(name: string): void {
    this.ImageFileName = name;
  }
  getFileName(): string {
    return this.ImageFileName;
  }
  setFileExtension(extension: string): void {
    this.ImageExtension = extension;
  }
  setData(data: string): void {
    this.ImageEncoded = data;
  }
  setError(err: any, message: string): void {
    this.errorMessage = message;
    console.log(this.errorMessage);
    console.error(err);
  }
  clear() {
    this.errorMessage = "";
    this.ImageFileName = "";
    this.ImageEncoded = "";
    this.ImageExtension = "";
  }
}

export class Device {
  public kind: TypeKizaiKind;
  public id: string;
  public name: string;
  public StatusUpdatedAt: string;
  public ComStatus: string;
  public ImageURL: string; // 地図URL
  public user: LoginUser; // 管理ページのみ使用
  constructor(data: any, user: LoginUser) {
    this.kind = data?.kind ?? "error";
    this.name = data?.name ?? "";
    if (this.name === "") {
      this.name = "【名称なし】";
    }
    this.id = data?.id ?? "";
    this.StatusUpdatedAt = data?.StatusUpdatedAt ?? "";
    this.ComStatus = data?.ComStatus ?? "";
    this.ImageURL = data?.ImageURL ?? "";
    this.user = user;
  }
}

export class FilterItem {
  public name: string;
  public bShow: boolean;
  public constructor(name: string, bShow: boolean) {
    this.name = name;
    this.bShow = bShow;
  }
}

export class FilterSetting {
  public name: string;
  public items: FilterItem[] = [];
  public constructor(name: string) {
    this.name = name;
  }
  public Add(item: FilterItem) {
    // 重複せずに追加。ただし表示設定(bShow)を無視
    if (this.items.find((value: FilterItem) => value.name === item.name)) {
      return;
    }
    this.items.push(item);
  }
}

export class AndonFilterSettings {
  public buildings: FilterSetting[] = [];
  public sensors: FilterSetting[] = [];
  public pico3: FilterSetting[] = [];
  public constructor() {
    this.Clear();
  }
  public Clear() {
    this.buildings = [];
    this.sensors = [];
    this.pico3 = [];
    this.buildings.push(new FilterSetting("STATUS"));
    this.buildings.push(new FilterSetting("建物名"));
    this.sensors.push(new FilterSetting("STATUS"));
    this.sensors.push(new FilterSetting("端末名称"));
    this.sensors.push(new FilterSetting("建物名"));
    this.sensors.push(new FilterSetting("設置場所"));
    this.pico3.push(new FilterSetting("STATUS"));
    this.pico3.push(new FilterSetting("端末名称"));
    this.pico3.push(new FilterSetting("建物名"));
    this.pico3.push(new FilterSetting("設置場所"));
  }
}
